import React, { useState, useEffect } from 'react';
import { LocationOption, DetailContent } from '../types';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

interface LocationCreatorProps {
  onAdd: (location: LocationOption) => void;
  onCancel: () => void;
  initialLocation?: LocationOption;
}

export function LocationCreator({ onAdd, onCancel, initialLocation }: LocationCreatorProps) {
  const [location, setLocation] = useState<Partial<LocationOption>>({
    type: "LOCATION",
    title: "",
    subtitle: "",
    imgUrl: "",
    locationId: "",
    label: "",
    description: "",
    detail_content: []
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (initialLocation) {
      setLocation(initialLocation);
    }
  }, [initialLocation]);

  const handleChange = (field: keyof LocationOption, value: string) => {
    setLocation(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    console.log('Submitting location:', location);
    const newErrors: Record<string, boolean> = {
      title: !location.title,
      subtitle: !location.subtitle,
      locationId: !location.locationId,
      label: !location.label,
      description: !location.description
    };
    
    setErrors(newErrors);
    console.log('Validation errors:', newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some(error => error)) {
      console.log('Missing required fields:', Object.keys(newErrors).filter(key => newErrors[key]));
      return;
    }

    console.log('All fields valid, calling onAdd with location:', location);
    onAdd(location as LocationOption);
  };

  return (
    <Card className="w-full">
      <CardContent className="space-y-4 pt-4">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            value={location.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="e.g., Taxi"
            className={errors.title ? "border-red-500" : ""}
          />
          {errors.title && <p className="text-sm text-red-500">Title is required</p>}
        </div>

        <div className="space-y-2">
          <Label>Subtitle</Label>
          <Input
            value={location.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
            placeholder="e.g., Taxi pickup zones"
            className={errors.subtitle ? "border-red-500" : ""}
          />
          {errors.subtitle && <p className="text-sm text-red-500">Subtitle is required</p>}
        </div>

        <div className="space-y-2">
          <Label>Image URL (Optional)</Label>
          <Input
            value={location.imgUrl}
            onChange={(e) => handleChange('imgUrl', e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div className="space-y-2">
          <Label>Location ID</Label>
          <Input
            value={location.locationId}
            onChange={(e) => handleChange('locationId', e.target.value)}
            placeholder="e.g., 4004200"
            className={errors.locationId ? "border-red-500" : ""}
          />
          {errors.locationId && <p className="text-sm text-red-500">Location ID is required</p>}
        </div>

        <div className="space-y-2">
          <Label>Label</Label>
          <Input
            value={location.label}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder="e.g., Fastest"
            className={errors.label ? "border-red-500" : ""}
          />
          {errors.label && <p className="text-sm text-red-500">Label is required</p>}
        </div>

        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={location.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="e.g., 3 min. walk time • $$$"
            className={errors.description ? "border-red-500" : ""}
          />
          {errors.description && <p className="text-sm text-red-500">Description is required</p>}
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {initialLocation ? 'Save Changes' : 'Add Location'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
